import { h, FunctionalComponent } from 'preact';
import register from 'preact-custom-element';
import { useCallback, useRef, useState } from 'preact/hooks';
import { Reveal } from '../util-components/Reveal';
import { TocMobileButton } from './TocMobileButton';
import { TocMobileItem } from './TocMobileItem';
import { ParsedTocPropsMobile, UnparsedTocPropsMobile } from './TocPropsTypes';

const parseProps = (unparsedProps: UnparsedTocPropsMobile): ParsedTocPropsMobile => {
	const items = JSON.parse(unparsedProps.items);
	const parsedProps: ParsedTocPropsMobile = {
		button: JSON.parse(unparsedProps.button),
		items: items,
	};
	return parsedProps;
};

export const TocMobile: FunctionalComponent<UnparsedTocPropsMobile> = (unparsedProps) => {
	const props = parseProps(unparsedProps);
	const { items, button } = props;

	const buttonRef = useRef<HTMLButtonElement>(null);
	const navRef = useRef<HTMLElement>(null);
	const [expanded, setExpanded] = useState(false);
	const [currentIndex, setCurrentIndex] = useState(0);
	const [animationDone, setAnimationDone] = useState(false);

	const open = useCallback(() => setExpanded(true), []);
	const close = useCallback((resetFocus = true) => {
		setExpanded(false);
		setCurrentIndex(0);
		setAnimationDone(false);
		if (resetFocus) {
			buttonRef.current?.focus();
		}
	}, []);

	const moveDown = useCallback(
		() =>
			setCurrentIndex((prev) => {
				if (prev === items.length - 1) {
					return 0;
				} else {
					return prev + 1;
				}
			}),
		[items.length],
	);

	const moveUp = useCallback(
		() =>
			setCurrentIndex((prev) => {
				if (prev === 0) {
					return items.length - 1;
				} else {
					return prev - 1;
				}
			}),
		[items.length],
	);

	const controlsId = 'toc-list';

	const keyDownHandler = useCallback(
		(e: KeyboardEvent) => {
			const key = e.key;
			if (key === 'ArrowDown') {
				moveDown();
				return;
			}
			if (key === 'ArrowUp') {
				moveUp();
				return;
			}
			if (key === 'Escape') {
				close();
				return;
			}
		},
		[moveUp, moveDown, close],
	);

	const handleButtonToggle = useCallback(() => {
		if (expanded) {
			close();
		} else {
			open();
		}
	}, [close, open, expanded]);

	const handleBlur = useCallback(
		(e: FocusEvent) => {
			const nextFocusedElement = e.relatedTarget as HTMLElement;
			if (!navRef.current?.contains(nextFocusedElement)) {
				close(false);
			}
		},
		[close],
	);
	const onShow = () => {
		setAnimationDone(true);
	};
	return (
		<div className="SP-Toc__box--mobile SP-Toc__box">
			<nav ref={navRef} onBlur={handleBlur} className="SP-Toc">
				<TocMobileButton ref={buttonRef} expanded={expanded} toggle={handleButtonToggle} controls={controlsId} {...button} />
				<Reveal duration={300} in={expanded} className="SP-Toc__reveal" onShow={onShow}>
					<ol id={controlsId} className="SP-TocList__list" onKeyDown={keyDownHandler}>
						{items.map((item, index) => (
							<TocMobileItem hasFocus={currentIndex === index && animationDone} label={item.label} href={item.href} key={item.href} />
						))}
					</ol>
				</Reveal>
			</nav>
		</div>
	);
};
register(TocMobile, 'sp-toc-mobile', ['button', 'items'], { shadow: false });
